/**
 * Global Styles for Layout System
 * Provides consistent styling utilities across all portals
 */

import { alpha } from '@mui/material/styles';

/**
 * Common animation easing functions
 */
export const EASING = {
  STANDARD: 'cubic-bezier(0.4, 0, 0.2, 1)',
  DECELERATE: 'cubic-bezier(0.0, 0, 0.2, 1)',
  ACCELERATE: 'cubic-bezier(0.4, 0, 1, 1)',
  SHARP: 'cubic-bezier(0.4, 0, 0.6, 1)',
  BOUNCE: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
};

/**
 * Common transition durations
 */
export const DURATION = {
  FAST: '0.15s',
  NORMAL: '0.2s',
  SLOW: '0.3s',
  SLOWER: '0.5s',
};

/**
 * Glass morphism styles
 */
export const createGlassMorphism = (themeConfig, isDark, intensity = 1) => ({
  backgroundColor: alpha(
    isDark ? themeConfig.glassMorphismDark : themeConfig.glassMorphismLight,
    0.8 * intensity
  ),
  backdropFilter: `blur(${12 * intensity}px) saturate(${180 * intensity}%)`,
  WebkitBackdropFilter: `blur(${12 * intensity}px) saturate(${180 * intensity}%)`,
  border: `1px solid ${alpha(isDark ? themeConfig.borderDark : themeConfig.borderLight, 0.8)}`,
  borderRadius: 12,
  boxShadow: isDark
    ? `0 ${8 * intensity}px ${32 * intensity}px 0 rgba(0, 0, 0, ${0.3 * intensity})`
    : `0 ${8 * intensity}px ${32 * intensity}px 0 rgba(31, 38, 135, ${0.2 * intensity})`,
});

/**
 * Gradient background styles
 */
export const createGradientBackground = (themeConfig, isDark, direction = '135deg') => ({
  background: isDark ? themeConfig.gradientDark : themeConfig.gradientLight,
  backgroundAttachment: 'fixed',
  backgroundSize: '400% 400%',
  animation: 'gradientShift 15s ease infinite',
});

/**
 * Hover effects
 */
export const HOVER_EFFECTS = {
  LIFT: {
    transition: `transform ${DURATION.NORMAL} ${EASING.STANDARD}`,
    '&:hover': {
      transform: 'translateY(-2px)',
    },
  },
  SCALE: {
    transition: `transform ${DURATION.NORMAL} ${EASING.STANDARD}`,
    '&:hover': {
      transform: 'scale(1.02)',
    },
  },
  GLOW: (color, isDark) => ({
    transition: `box-shadow ${DURATION.NORMAL} ${EASING.STANDARD}`,
    '&:hover': {
      boxShadow: `0 0 20px ${alpha(color, isDark ? 0.3 : 0.2)}`,
    },
  }),
  SLIDE_RIGHT: {
    transition: `transform ${DURATION.NORMAL} ${EASING.STANDARD}`,
    '&:hover': {
      transform: 'translateX(4px)',
    },
  },
};

/**
 * Focus states
 */
export const FOCUS_STYLES = (color) => ({
  '&:focus-visible': {
    outline: `2px solid ${color}`,
    outlineOffset: '2px',
  },
});

/**
 * Card styles with consistent shadows and borders
 */
export const createCardStyles = (themeConfig, isDark, elevation = 1) => ({
  ...createGlassMorphism(themeConfig, isDark, elevation),
  ...HOVER_EFFECTS.LIFT,
  '&:hover': {
    ...HOVER_EFFECTS.LIFT['&:hover'],
    boxShadow: isDark
      ? `0 ${12 * elevation}px ${40 * elevation}px 0 rgba(0, 0, 0, ${0.4 * elevation})`
      : `0 ${12 * elevation}px ${40 * elevation}px 0 rgba(31, 38, 135, ${0.3 * elevation})`,
  },
});

/**
 * Button styles with portal theming
 */
export const createButtonStyles = (themeConfig, isDark, variant = 'contained') => {
  const baseStyles = {
    borderRadius: 10,
    fontWeight: 500,
    fontSize: '0.95rem',
    padding: '10px 24px',
    textTransform: 'none',
    transition: `all ${DURATION.NORMAL} ${EASING.STANDARD}`,
    ...HOVER_EFFECTS.LIFT,
    ...FOCUS_STYLES(isDark ? themeConfig.primaryColorDark : themeConfig.primaryColor),
  };

  const variantStyles = {
    contained: {
      ...baseStyles,
      backgroundColor: isDark ? themeConfig.primaryColorDark : themeConfig.primaryColor,
      color: isDark ? '#000' : '#fff',
      boxShadow: `0 4px 12px ${alpha(
        isDark ? themeConfig.primaryColorDark : themeConfig.primaryColor,
        0.3
      )}`,
      '&:hover': {
        ...baseStyles['&:hover'],
        boxShadow: `0 6px 16px ${alpha(
          isDark ? themeConfig.primaryColorDark : themeConfig.primaryColor,
          0.4
        )}`,
      },
    },
    outlined: {
      ...baseStyles,
      border: `2px solid ${isDark ? themeConfig.primaryColorDark : themeConfig.primaryColor}`,
      color: isDark ? themeConfig.primaryColorDark : themeConfig.primaryColor,
      backgroundColor: 'transparent',
      '&:hover': {
        ...baseStyles['&:hover'],
        backgroundColor: alpha(
          isDark ? themeConfig.primaryColorDark : themeConfig.primaryColor,
          0.1
        ),
      },
    },
    text: {
      ...baseStyles,
      color: isDark ? themeConfig.primaryColorDark : themeConfig.primaryColor,
      backgroundColor: 'transparent',
      '&:hover': {
        ...baseStyles['&:hover'],
        backgroundColor: alpha(
          isDark ? themeConfig.primaryColorDark : themeConfig.primaryColor,
          0.08
        ),
      },
    },
  };

  return variantStyles[variant] || variantStyles.contained;
};

/**
 * Input field styles
 */
export const createInputStyles = (themeConfig, isDark) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: alpha(isDark ? themeConfig.paperDark : themeConfig.paperLight, 0.8),
    backdropFilter: 'blur(8px)',
    transition: `all ${DURATION.NORMAL} ${EASING.STANDARD}`,
    '&:hover': {
      backgroundColor: alpha(isDark ? themeConfig.paperDark : themeConfig.paperLight, 0.9),
    },
    '&.Mui-focused': {
      backgroundColor: isDark ? themeConfig.paperDark : themeConfig.paperLight,
      boxShadow: `0 0 0 2px ${alpha(
        isDark ? themeConfig.primaryColorDark : themeConfig.primaryColor,
        0.2
      )}`,
    },
  },
});

/**
 * Navigation item styles
 */
export const createNavigationItemStyles = (
  themeConfig,
  isDark,
  isActive = false,
  screenSize = 'desktop'
) => ({
  minHeight: screenSize === 'monitor' ? 56 : 48,
  px: screenSize === 'monitor' ? 3 : 2.5,
  borderRadius: 2,
  mx: 1,
  mb: 0.5,
  backgroundColor: isActive
    ? alpha(isDark ? themeConfig.primaryColorDark : themeConfig.primaryColor, isDark ? 0.15 : 0.1)
    : 'transparent',
  color: isActive ? (isDark ? themeConfig.primaryColorDark : themeConfig.primaryColor) : 'inherit',
  ...HOVER_EFFECTS.SLIDE_RIGHT,
  '&:hover': {
    ...HOVER_EFFECTS.SLIDE_RIGHT['&:hover'],
    backgroundColor: alpha(
      isDark ? themeConfig.primaryColorDark : themeConfig.primaryColor,
      isDark ? 0.1 : 0.05
    ),
  },
  '&.Mui-selected': {
    backgroundColor: alpha(
      isDark ? themeConfig.primaryColorDark : themeConfig.primaryColor,
      isDark ? 0.2 : 0.15
    ),
    '&:hover': {
      backgroundColor: alpha(
        isDark ? themeConfig.primaryColorDark : themeConfig.primaryColor,
        isDark ? 0.25 : 0.2
      ),
    },
  },
});

/**
 * Scrollbar styles
 */
export const createScrollbarStyles = (themeConfig, isDark) => ({
  scrollbarColor: isDark ? '#6b6b6b #2b2b2b' : '#959595 #f5f5f5',
  '&::-webkit-scrollbar': {
    width: '8px',
    backgroundColor: isDark ? '#2b2b2b' : '#f5f5f5',
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: 8,
    backgroundColor: isDark ? '#6b6b6b' : '#959595',
    border: `2px solid ${isDark ? '#2b2b2b' : '#f5f5f5'}`,
    '&:hover': {
      backgroundColor: isDark ? '#959595' : '#6b6b6b',
    },
  },
});

/**
 * Portal-specific accent colors
 */
export const PORTAL_ACCENTS = {
  bureau: {
    light: '#1976d2',
    dark: '#90caf9',
  },
  admin: {
    light: '#1976d2',
    dark: '#90caf9',
  },
  client: {
    light: '#2e7d32',
    dark: '#66bb6a',
  },
  'super-admin': {
    light: '#d32f2f',
    dark: '#ef5350',
  },
};

/**
 * Create portal-specific styles
 */
export const createPortalStyles = (portalType, themeConfig, isDark) => ({
  accentColor: isDark ? themeConfig.primaryColorDark : themeConfig.primaryColor,
  gradientText: {
    background: isDark
      ? `linear-gradient(45deg, ${themeConfig.primaryColorDark}, ${themeConfig.accentColor})`
      : `linear-gradient(45deg, ${themeConfig.primaryColor}, ${themeConfig.accentColor})`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  glassMorphism: createGlassMorphism(themeConfig, isDark),
  gradientBackground: createGradientBackground(themeConfig, isDark),
  card: createCardStyles(themeConfig, isDark),
  button: {
    contained: createButtonStyles(themeConfig, isDark, 'contained'),
    outlined: createButtonStyles(themeConfig, isDark, 'outlined'),
    text: createButtonStyles(themeConfig, isDark, 'text'),
  },
  input: createInputStyles(themeConfig, isDark),
  scrollbar: createScrollbarStyles(themeConfig, isDark),
});

/**
 * CSS keyframes for animations
 */
export const ANIMATIONS = {
  gradientShift: `
    @keyframes gradientShift {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
  `,
  fadeIn: `
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `,
  slideIn: `
    @keyframes slideIn {
      from { transform: translateX(-100%); }
      to { transform: translateX(0); }
    }
  `,
  scaleIn: `
    @keyframes scaleIn {
      from { transform: scale(0.9); opacity: 0; }
      to { transform: scale(1); opacity: 1; }
    }
  `,
};

export default {
  EASING,
  DURATION,
  HOVER_EFFECTS,
  FOCUS_STYLES,
  PORTAL_ACCENTS,
  ANIMATIONS,
  createGlassMorphism,
  createGradientBackground,
  createCardStyles,
  createButtonStyles,
  createInputStyles,
  createNavigationItemStyles,
  createScrollbarStyles,
  createPortalStyles,
};
